import React, {
  SyntheticEvent,
  useCallback,
  useId,
  useMemo,
  useState,
} from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Link } from 'react-router-dom';
import { MenuItem } from '@mui/material';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import BuxLogo from '@app/illu/Branding/SingleLetterLogoBux.svg';

import { setCurrentOrganization as setCurrentOrganizationAction } from 'store';
import { MenuWindow, GenericButton, NAVBAR_TOOLTIP_ID } from '@app/core';
import { Theme } from '@app/theme';
import {
  useCurrentOrganizationId,
  useMe,
  useOrganizations,
  useSetCurrentOrganizationId,
} from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  logo: {
    ...theme.flexCenter,

    width: theme.height.small,
    height: theme.height.small,

    borderRadius: '50%',
    overflow: 'hidden',

    backgroundColor: theme.colors.white,
    color: theme.colors.green,

    marginBottom: theme.marginBase * 3,

    [theme.media.mobile]: {
      display: 'none',
    },
    '& > i': {
      fontSize: theme.iconSize.large,
    },
  },
  orgaName: {
    ...theme.fonts.h4,
    color: theme.colors.greenAccessible,
  },
}));

export const OrganizationSwitcher = () => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const dispatch = useDispatch();

  const [anchorElmnt, setAnchorElmnt] = useState<null | Element>(null);
  const id = useId();
  const { data: me } = useMe();
  const setCurrentOrganization = useSetCurrentOrganizationId();
  const { data: organizations } = useOrganizations(
    me?.organizations.map(({ organizationId }) => organizationId),
  );
  const currentOrganizationId = useCurrentOrganizationId();

  const currentOrganization = useMemo(() => {
    return organizations?.find(({ id }) => id === currentOrganizationId);
  }, [currentOrganizationId, organizations]);

  const onClick = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      setAnchorElmnt(event.currentTarget);
    },
    [setAnchorElmnt],
  );
  const onClose = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      setAnchorElmnt(null);
    },
    [setAnchorElmnt],
  );

  if (!organizations || organizations.length === 1) {
    return (
      <Link to="/" className={classes.logo}>
        <img src={BuxLogo} />
      </Link>
    );
  }
  return (
    <>
      <GenericButton
        onClick={onClick}
        className={classNames(classes.logo, classes.orgaName)}
        data-tip={currentOrganization?.name}
        data-for={NAVBAR_TOOLTIP_ID}
      >
        {currentOrganization?.name.slice(0, 1)}
      </GenericButton>
      <MenuWindow
        open={!!anchorElmnt}
        onClose={onClose}
        id={id}
        anchorEl={anchorElmnt}
        onClick={onClose}
      >
        {organizations.map((organization) => (
          <MenuItem
            onClick={() => {
              setCurrentOrganization(organization.id);
              dispatch(setCurrentOrganizationAction(organization.id));
            }}
            selected={currentOrganizationId === organization.id}
          >
            {organization.name}
          </MenuItem>
        ))}
      </MenuWindow>
    </>
  );
};
